<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="$insertAccess($route.fullPath)">
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-book-open-variant</v-icon>
                Muster Roll / Attendance Ledger
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport" ref="filterForm">
                  <v-row class="py-3 px-3" align="end">
                  <v-col cols="12" sm="6" md="3">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date"
                      label="From Date"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      hide-details="auto"
                      required
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date"
                      label="To Date"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      hide-details="auto"
                      required
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                      v-model="filters.employee_id"
                      :items="employees"
                      :item-title="formatEmployeeLabel"
                      item-value="id"
                      label="Employee (Optional)"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      clearable
                      hide-details="auto"
                      :loading="employeesLoading"
                      placeholder="Search employee"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="3" class="d-flex align-end">
                    <v-btn
                      type="submit"
                      color="primary"
                      variant="flat"
                      :loading="loading"
                      prepend-icon="mdi-magnify"
                      density="compact"
                      height="40"
                    >
                      Search
                    </v-btn>
                  </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Report Table -->
          <v-expansion-panels v-model="reportPanel" class="mt-2">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                expand-icon="mdi-chevron-down"
                >
                <v-icon class="me-2">mdi-table</v-icon>
                Report Data
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        v-if="reportData.length > 0"
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToExcel"
                          :disabled="!reportData.length"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          :disabled="!reportData.length"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>
                </v-row>

                <v-progress-linear
                  v-if="loading"
                  indeterminate
                  color="primary"
                  class="mb-4"
                />

                <v-alert
                  v-if="!loading && filteredReportData.length === 0"
                  type="info"
                  variant="tonal"
                >
                  No attendance ledger data found for the selected criteria.
                </v-alert>

                <div v-if="filteredReportData.length > 0 && dateColumns.length > 0" class="table-responsive" style="overflow-x: auto;">
                  <v-table density="comfortable" class="text-caption table-bordered" style="min-width: 100%;">
                    <thead>
                      <tr>
                        <th rowspan="2" style="min-width: 50px;"># SL</th>
                        <th rowspan="2" style="min-width: 100px;">Employee Code</th>
                        <th rowspan="2" style="min-width: 150px;">Employee Name</th>
                        <th rowspan="2" style="min-width: 120px;">Department</th>
                        <th rowspan="2" style="min-width: 120px;">Designation</th>
                        <th :colspan="dateColumns.length" style="text-align: center; border-bottom: 1px solid #000;">
                          Dates
                        </th>
                        <th rowspan="2" style="min-width: 80px;">Total Present</th>
                        <th rowspan="2" style="min-width: 80px;">Total Absent</th>
                        <th rowspan="2" style="min-width: 80px;">Total Leave</th>
                        <th rowspan="2" style="min-width: 100px;">Total OT (hrs)</th>
                      </tr>
                      <tr>
                        <th v-for="date in dateColumns" :key="date" style="min-width: 50px; text-align: center; font-size: 10px;">
                          {{ formatDateColumn(date) }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in filteredReportData" :key="`${row.employee_code}-${index}`">
                        <td>{{ index + 1 }}</td>
                        <td class="font-weight-medium">{{ row.employee_code || '-' }}</td>
                        <td class="font-weight-medium">{{ row.employee_name || '-' }}</td>
                        <td>{{ row.department || '-' }}</td>
                        <td>{{ row.designation || '-' }}</td>
                        <td 
                          v-for="date in dateColumns" 
                          :key="`${row.employee_code}-${date}`"
                          style="text-align: center; font-weight: bold;"
                          :class="getStatusClass(row[date])"
                        >
                          {{ row[date] || '' }}
                        </td>
                        <td style="text-align: center;">{{ formatNumber(row.total_present) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.total_absent) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.total_leave) }}</td>
                        <td style="text-align: center;">{{ formatNumber(row.total_ot_hours, 2) }}</td>
                      </tr>
                    </tbody>
                    <tfoot v-if="filteredReportData.length > 0">
                      <tr style="font-weight: bold; color: #000000;">
                        <td :colspan="5 + dateColumns.length" style="text-align: right; padding: 12px;">Total:</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.total_present) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.total_absent) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.total_leave) }}</td>
                        <td style="text-align: center; padding: 12px;">{{ formatNumber(filteredTotals.total_ot_hours, 2) }}</td>
                      </tr>
                    </tfoot>
                  </v-table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Legend -->
          <v-card v-if="reportData.length > 0" variant="outlined" class="mt-4">
            <v-card-text class="pa-3">
              <div class="text-caption">
                <strong>Legend:</strong>
                <span class="ms-2"><strong>P</strong> = Present</span>
                <span class="ms-2"><strong>L</strong> = Late / Leave</span>
                <span class="ms-2"><strong>E</strong> = Early</span>
                <span class="ms-2"><strong>LE</strong> = Late & Early</span>
                <span class="ms-2"><strong>A</strong> = Absent</span>
                <span class="ms-2"><strong>W</strong> = Weekend</span>
                <span class="ms-2"><strong>H</strong> = Holiday</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue';
import { toast } from 'vue3-toastify';
import { useStore } from 'vuex';
import { formatDateView, formatDateDash } from "@/plugins/helpers";


const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

// Loading states
const loading = ref(false)
const employeesLoading = ref(false)
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

// Filters
const filters = reactive({
  from_date: '',
  to_date: '',
  employee_id: null
})

// Data
const reportData = ref([])
const dateColumns = ref([])
const employees = ref([])

const formatEmployeeLabel = item => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code ? ` - ${item.employee_code}` : ''}`
}

const formatNumber = (value, fraction = 0) => {
  if (value === undefined || value === null || isNaN(value)) return '-'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const formatDateColumn = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.getDate().toString()
}

const getStatusClass = (status) => {
  if (!status) return ''
  const statusMap = {
    'P': 'text-success',
    'L': 'text-warning',
    'E': 'text-info',
    'LE': 'text-orange',
    'A': 'text-error',
    'W': 'text-grey',
    'H': 'text-primary'
  }
  return statusMap[status] || ''
}

// Filtered report data based on search
const filteredReportData = computed(() => {
  if (!search.value) {
    return reportData.value
  }
  
  const searchLower = search.value.toLowerCase()
  return reportData.value.filter(row => {
    const employeeCode = (row.employee_code || '').toLowerCase()
    const employeeName = (row.employee_name || '').toLowerCase()
    const department = (row.department || '').toLowerCase()
    const designation = (row.designation || '').toLowerCase()
    return employeeCode.includes(searchLower) || 
           employeeName.includes(searchLower) || 
           department.includes(searchLower) || 
           designation.includes(searchLower)
  })
})

// Computed totals
const totals = computed(() => {
  return reportData.value.reduce(
    (acc, row) => {
      acc.total_present += Number(row.total_present) || 0
      acc.total_absent += Number(row.total_absent) || 0
      acc.total_leave += Number(row.total_leave) || 0
      acc.total_ot_hours += Number(row.total_ot_hours) || 0
      return acc
    },
    {
      total_present: 0,
      total_absent: 0,
      total_leave: 0,
      total_ot_hours: 0
    }
  )
})

// Computed totals for filtered data
const filteredTotals = computed(() => {
  return filteredReportData.value.reduce(
    (acc, row) => {
      acc.total_present += Number(row.total_present) || 0
      acc.total_absent += Number(row.total_absent) || 0
      acc.total_leave += Number(row.total_leave) || 0
      acc.total_ot_hours += Number(row.total_ot_hours) || 0
      return acc
    },
    {
      total_present: 0,
      total_absent: 0,
      total_leave: 0,
      total_ot_hours: 0
    }
  )
})

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})



const setDefaultDates = () => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  filters.from_date = firstDay
  filters.to_date = lastDay
}

// Methods
const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const loadReport = async () => {
  if (!filters.from_date || !filters.to_date) {
    toast.error('Please select both From Date and To Date')
    return
  }

  loading.value = true
  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
      employee_id: filters.employee_id || undefined
    }

    const response = await store.dispatch('getAttendanceLedgerReport', params)

    if (response.success) {
      reportData.value = response.data || []
      dateColumns.value = response.dates || []
      if (reportData.value.length === 0) {
        toast.info('No data found for the selected criteria')
      }
    } else {
      toast.error(response.message || 'Failed to load report')
      reportData.value = []
      dateColumns.value = []
    }
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || 'Failed to load report')
    reportData.value = []
    dateColumns.value = []
  } finally {
    loading.value = false
  }
}

// Export to Excel
const exportToExcel = () => {
  if (!reportData.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Muster Roll / Attendance Ledger Report'],
    [`From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`]
  ]
  
  if (filters.employee_id) {
    const selectedEmployee = employees.value.find(e => e.id === filters.employee_id)
    if (selectedEmployee) {
      data.push([`Employee: ${formatEmployeeLabel(selectedEmployee)}`])
    }
  }
  
  data.push([])
  
  // Header row 1
  const headerRow1 = ['# SL', 'Employee Code', 'Employee Name', 'Department', 'Designation']
  dateColumns.value.forEach(date => {
    headerRow1.push(formatDateColumn(date))
  })
  headerRow1.push('Total Present', 'Total Absent', 'Total Leave', 'Total OT (hrs)')
  data.push(headerRow1)
  
  // Header row 2 with full dates
  const headerRow2 = ['', '', '', '', '']
  dateColumns.value.forEach(date => {
    headerRow2.push(formatDateView(date))
  })
  headerRow2.push('', '', '', '')
  data.push(headerRow2)
  
  data.push([])

  // Add data rows
  reportData.value.forEach((row, index) => {
    const rowData = [
      index + 1,
      row.employee_code || '-',
      row.employee_name || '-',
      row.department || '-',
      row.designation || '-'
    ]
    dateColumns.value.forEach(date => {
      rowData.push(row[date] || '')
    })
    rowData.push(
      formatNumber(row.total_present),
      formatNumber(row.total_absent),
      formatNumber(row.total_leave),
      formatNumber(row.total_ot_hours, 2)
    )
    data.push(rowData)
  })

  // Add totals row
  if (reportData.value.length > 0) {
    data.push([])
    const totalsRow = ['', '', '', '', 'Total:']
    dateColumns.value.forEach(() => {
      totalsRow.push('')
    })
    totalsRow.push(
      formatNumber(totals.value.total_present),
      formatNumber(totals.value.total_absent),
      formatNumber(totals.value.total_leave),
      formatNumber(totals.value.total_ot_hours, 2)
    )
    data.push(totalsRow)
    
    // Add legend
    data.push([])
    data.push(['Legend: P = Present, L = Late/Leave, E = Early, LE = Late & Early, A = Absent, W = Weekend, H = Holiday'])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'attendance_ledger_report_' + proxy.$fileDate())
  }
}

// Export to PDF
const exportToPDF = async () => {
  if (!reportData.value.length) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value
  const selectedEmployee = filters.employee_id 
    ? employees.value.find(e => e.id === filters.employee_id)
    : null

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Muster Roll / Attendance Ledger Report</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; }
          .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
          .invoice-header img { max-height: 70px; max-width: 150px; }
          .table th, .table td { border: 1px solid #000; padding: 4px; vertical-align: middle; font-size: 10px; }
          .table th { background-color: #f0f0f0; color: #333; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          h4 { font-size: 20px; color: #333; }
          p { font-size: 14px; color: #555; margin: 2px 0; }
          strong { font-weight: bold; color: #333; }
          @page { margin: 0.5in; size: landscape; }
          .logo-container { display:flex; align-items:center; }
          .logo-container img { margin-left: 10px; }
          .table { width: 100%; border-collapse: collapse; table-layout: auto; }
          thead { display: table-header-group; }
          .totals-row { page-break-inside: avoid; }
          .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
          .status-p { color: #2e7d32; font-weight: bold; }
          .status-l { color: #f57c00; font-weight: bold; }
          .status-e { color: #1976d2; font-weight: bold; }
          .status-le { color: #ff6f00; font-weight: bold; }
          .status-a { color: #d32f2f; font-weight: bold; }
          .status-w { color: #757575; font-weight: bold; }
          .status-h { color: #7b1fa2; font-weight: bold; }
          .legend { font-size: 11px; margin-top: 10px; }
        </style>
      </head>
      <body>
        <div class="container-fluid position-relative">
          <div class="invoice-header">
            <div>
              <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
              <p style="width:65%">${company?.address || ''}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
              ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
            </div>
            <div class="logo-container">
              ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
            </div>
          </div>
          <h4 class="text-center my-4"><strong>MUSTER ROLL / ATTENDANCE LEDGER REPORT</strong></h4>
          <div class="row mb-3">
            <div class="col-sm-8">
              <p><strong>Period:</strong> ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}</p>
              ${selectedEmployee ? `<p><strong>Employee:</strong> ${formatEmployeeLabel(selectedEmployee)}</p>` : ''}
            </div>
            <div class="col-sm-4 text-end">
              <p><strong>Total Records:</strong> ${reportData.value.length}</p>
            </div>
          </div>
          <div style="overflow-x: auto;">
            <table class="table mt-4">
              <thead>
                <tr>
                  <th rowspan="2">SN</th>
                  <th rowspan="2">Employee Code</th>
                  <th rowspan="2">Employee Name</th>
                  <th rowspan="2">Department</th>
                  <th rowspan="2">Designation</th>
                  <th colspan="${dateColumns.value.length}" class="text-center">Dates</th>
                  <th rowspan="2">Total Present</th>
                  <th rowspan="2">Total Absent</th>
                  <th rowspan="2">Total Leave</th>
                  <th rowspan="2">Total OT (hrs)</th>
                </tr>
                <tr>
                  ${dateColumns.value.map(date => `<th class="text-center" style="font-size: 9px;">${formatDateView(date)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${reportData.value.map((row, index) => {
                  const getStatusClass = (status) => {
                    if (!status) return ''
                    const statusMap = {
                      'P': 'status-p',
                      'L': 'status-l',
                      'E': 'status-e',
                      'LE': 'status-le',
                      'A': 'status-a',
                      'W': 'status-w',
                      'H': 'status-h'
                    }
                    return statusMap[status] || ''
                  }
                  return `
                    <tr>
                      <td>${index + 1}</td>
                      <td><strong>${row.employee_code || '-'}</strong></td>
                      <td><strong>${row.employee_name || '-'}</strong></td>
                      <td>${row.department || '-'}</td>
                      <td>${row.designation || '-'}</td>
                      ${dateColumns.value.map(date => {
                        const status = row[date] || ''
                        const statusClass = getStatusClass(status)
                        return `<td class="text-center ${statusClass}">${status}</td>`
                      }).join('')}
                      <td class="text-center">${formatNumber(row.total_present)}</td>
                      <td class="text-center">${formatNumber(row.total_absent)}</td>
                      <td class="text-center">${formatNumber(row.total_leave)}</td>
                      <td class="text-center">${formatNumber(row.total_ot_hours, 2)}</td>
                    </tr>
                  `
                }).join('')}
                <tr class="totals-row">
                  <td colspan="${5 + dateColumns.value.length}" class="text-end">Total:</td>
                  <td class="text-center">${formatNumber(totals.value.total_present)}</td>
                  <td class="text-center">${formatNumber(totals.value.total_absent)}</td>
                  <td class="text-center">${formatNumber(totals.value.total_leave)}</td>
                  <td class="text-center">${formatNumber(totals.value.total_ot_hours, 2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="legend">
            <strong>Legend:</strong>
            <span class="status-p">P = Present</span>,
            <span class="status-l">L = Late/Leave</span>,
            <span class="status-e">E = Early</span>,
            <span class="status-le">LE = Late & Early</span>,
            <span class="status-a">A = Absent</span>,
            <span class="status-w">W = Weekend</span>,
            <span class="status-h">H = Holiday</span>
          </div>
        </div>
      </body>
    </html>
  `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}

// Initialize
onMounted(async () => {
  await loadEmployees()
  setDefaultDates()
})
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.gap-2 {
  gap: 8px;
}

.panel-header {
  font-weight: 600;
}

.table-responsive {
  max-width: 100%;
  overflow-x: auto;
}

.text-orange {
  color: #ff6f00 !important;
}
</style>

